import { Icon } from "@material-ui/core";
import React, { Fragment } from "react";
import styled from "styled-components";

import {
    buildFolderPathSegments,
} from "js/editor/PresentationLibrary/utils/folderPaths";
import { IWorkspaceResourceFolder } from "common/interfaces";
import { themeColors } from "js/react/sharedStyles";

const BreadcrumbContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: "Source Sans Pro";
    font-size: 14px;
    flex-shrink: 0;
    line-height: 125%;
    white-space: nowrap;
`;

const SimpleBreadcrumbText = styled.div`
    color: #222;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-weight: 600;
    line-height: 125%;
    white-space: nowrap;
`;

const FolderIcon = styled(Icon)`
    &&& {
        font-size: 18px;
        color: #666;
    }
`;

const BreadcrumbSegmentText = styled.span<{ $isCurrent?: boolean }>`
    color: ${props => props.$isCurrent ? "#222" : "#888"};
    font-weight: ${props => props.$isCurrent ? 600 : 400};
`;

const BreadcrumbSegmentButton = styled.button`
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    font: inherit;
    cursor: pointer;
    color: #888;
    font-weight: 400;
    font-size: 14px;
    line-height: 125%;

    &:hover {
        color: ${themeColors.ui_blue};
        text-decoration: underline;
    }
`;

const BreadcrumbSeparator = styled(Icon)`
    &&& {
        font-size: 18px;
        color: #666;
    }
`;

export type PresentationLibraryPaneBreadcrumbProps = {
    breadcrumb: string | string[];
    showFolderIcon?: boolean;
    folderIds?: string[];
    onSelectFolder?: (folderId: string) => void;
    onSelectSegment?: (index: number) => void;
};

export type PresentationLibraryPaneBreadcrumbData = PresentationLibraryPaneBreadcrumbProps;

export function PresentationLibraryPaneBreadcrumb({
    breadcrumb,
    showFolderIcon,
    folderIds,
    onSelectFolder,
    onSelectSegment,
}: PresentationLibraryPaneBreadcrumbProps) {
    if (typeof breadcrumb === "string") {
        return (
            <SimpleBreadcrumbText data-test-id="presentation-library-breadcrumb">
                {breadcrumb}
            </SimpleBreadcrumbText>
        );
    }

    if (!breadcrumb.length) {
        return null;
    }

    const getSegmentClickHandler = (index: number) => {
        if (index >= breadcrumb.length - 1) {
            return undefined;
        }

        if (onSelectSegment) {
            return () => onSelectSegment(index);
        }

        const folderId = folderIds?.[index];
        if (onSelectFolder && folderId) {
            return () => onSelectFolder(folderId);
        }

        return undefined;
    };

    return (
        <BreadcrumbContainer data-test-id="presentation-library-breadcrumb">
            {showFolderIcon && (
                <FolderIcon className="material-symbols-outlined">folder</FolderIcon>
            )}
            {breadcrumb.map((label, index) => {
                const isCurrent = index === breadcrumb.length - 1;
                const onClick = getSegmentClickHandler(index);

                return (
                    <Fragment key={`${label}-${index}`}>
                        {index > 0 && (
                            <BreadcrumbSeparator className="material-symbols-outlined">
                                chevron_right
                            </BreadcrumbSeparator>
                        )}
                        {onClick ? (
                            <BreadcrumbSegmentButton type="button" onClick={onClick}>
                                {label}
                            </BreadcrumbSegmentButton>
                        ) : (
                            <BreadcrumbSegmentText $isCurrent={isCurrent}>
                                {label}
                            </BreadcrumbSegmentText>
                        )}
                    </Fragment>
                );
            })}
        </BreadcrumbContainer>
    );
}

export function buildFolderBreadcrumbData(
    folderId: string,
    resourceFolders: IWorkspaceResourceFolder[],
    onSelectFolder: (folderId: string) => void,
): PresentationLibraryPaneBreadcrumbProps {
    const pathSegments = buildFolderPathSegments(folderId, resourceFolders);

    return {
        breadcrumb: pathSegments.map(segment => segment.name),
        showFolderIcon: true,
        folderIds: pathSegments.map(segment => segment.id),
        onSelectFolder,
    };
}

export function buildResourcePaneBreadcrumbData({
    rootLabel,
    selectedFolderId,
    resourceFolders,
    onSelectFolder,
}: {
    rootLabel: string;
    selectedFolderId: string | null;
    resourceFolders: IWorkspaceResourceFolder[];
    onSelectFolder: (folderId: string | null) => void;
}): PresentationLibraryPaneBreadcrumbProps {
    if (selectedFolderId) {
        const pathSegments = buildFolderPathSegments(selectedFolderId, resourceFolders);
        if (pathSegments.length) {
            return {
                breadcrumb: pathSegments.map(segment => segment.name),
                showFolderIcon: true,
                folderIds: pathSegments.map(segment => segment.id),
                onSelectFolder: folderId => onSelectFolder(folderId),
            };
        }
    }

    return { breadcrumb: rootLabel };
}
